import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSnowflake,
    faSearch,
    faSpinner,
    faTimes,
    faHistory,
    faChevronDown,
    faChevronUp,
    faChevronLeft,
    faChevronRight,
    faFire,
} from '@fortawesome/free-solid-svg-icons';
import Api from '../../Api';
import { useModalDismiss } from '../../hooks/useModalDismiss';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d.replace(' ', 'T')).toLocaleDateString('fr-FR') : '');

const PERSON_TYPE_LABELS = { interviewer: 'Enquêteur CATI', supervisor: 'Prestataire F2F', controller: 'Contrôleur' };

const STATUS = {
    frozen: { label: 'Gelé', cls: 'bg-sky-100 text-sky-700' },
    unfrozen: { label: 'Dégelé — en attente de paiement', cls: 'bg-amber-100 text-amber-700' },
    paid: { label: 'Payé', cls: 'bg-green-100 text-green-700' },
    merged: { label: 'Regroupé dans un autre gel', cls: 'bg-gray-100 text-gray-600' },
    cancelled: { label: 'Annulé', cls: 'bg-red-100 text-red-700' },
};

const EVENT_LABELS = {
    freeze_requested: 'Gel demandé (brouillon)',
    freeze_cancelled: 'Gel annulé avant confirmation',
    frozen: 'Gel confirmé',
    debt_held: 'Dette mise en attente avec le montant gelé',
    unfrozen_manual: 'Dégelé manuellement',
    unfrozen_auto: 'Dégelé automatiquement (reprise d\'activité)',
    reported: 'Non payé : montant reporté',
    paid: 'Payé',
    refrozen: 'Gelé à nouveau',
    cancelled: 'Ligne annulée',
};

/**
 * "Reports gelés" tab of the unpaid page: amounts frozen with "Geler" in a batch, their history,
 * and manual unfreeze (section heads, for their own section).
 */
const FrozenPayments = ({ navigate }) => {
    const [view, setView] = useState('current'); // current | history
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [persons, setPersons] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    // Which view the shown data belongs to: the spinner replaces the list only until that view has loaded once,
    // later refetches (search, status filter, paging) keep the list on screen.
    const [loadedView, setLoadedView] = useState(null);
    const [error, setError] = useState('');

    const [history, setHistory] = useState([]);
    const [historyStatus, setHistoryStatus] = useState('');
    const [historyPage, setHistoryPage] = useState(1);
    const [historyLastPage, setHistoryLastPage] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [expanded, setExpanded] = useState({});

    const [unfreezeTarget, setUnfreezeTarget] = useState(null); // { person, caseItem }
    const [unfreezeReason, setUnfreezeReason] = useState('');
    const [unfreezing, setUnfreezing] = useState(false);
    const [notice, setNotice] = useState('');
    const unfreezeDismiss = useModalDismiss(() => setUnfreezeTarget(null), { backdropClose: false, enabled: !!unfreezeTarget && !unfreezing });

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    const fetchCurrent = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = new URLSearchParams();
            if (debouncedSearch) params.append('search', debouncedSearch);
            const res = await Api.get(`/payments/unpaid/frozen?${params}`);
            setPersons(res.data?.data || []);
            setStats(res.data?.stats || null);
            setLoadedView('current');
        } catch (e) {
            setError(e.response?.data?.message || 'Erreur lors du chargement des montants gelés');
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch]);

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = new URLSearchParams({ page: historyPage, per_page: 20 });
            if (debouncedSearch) params.append('search', debouncedSearch);
            if (historyStatus) params.append('status', historyStatus);
            const res = await Api.get(`/payments/unpaid/frozen/history?${params}`);
            setHistory(res.data?.data || []);
            setHistoryLastPage(res.data?.pagination?.last_page || 1);
            setHistoryTotal(res.data?.pagination?.total || 0);
            setLoadedView('history');
        } catch (e) {
            setError(e.response?.data?.message || 'Erreur lors du chargement de l\'historique');
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, historyStatus, historyPage]);

    useEffect(() => {
        if (view === 'current') fetchCurrent();
        else fetchHistory();
    }, [view, fetchCurrent, fetchHistory]);

    useEffect(() => { setHistoryPage(1); }, [debouncedSearch, historyStatus]);

    const submitUnfreeze = async () => {
        if (!unfreezeTarget || !unfreezeReason.trim()) return;
        try {
            setUnfreezing(true);
            const res = await Api.post(`/payments/unpaid/frozen/${unfreezeTarget.caseItem.id}/unfreeze`, { reason: unfreezeReason.trim() });
            setNotice(res.data?.message || 'Montant dégelé');
            setUnfreezeTarget(null);
            setUnfreezeReason('');
            fetchCurrent();
        } catch (e) {
            setError(e.response?.data?.message || 'Erreur lors du dégel');
        } finally {
            setUnfreezing(false);
        }
    };

    const BatchLink = ({ id }) => (id ? (
        <button onClick={() => navigate(`/payments/${id}`)} className="text-blue-600 hover:text-blue-800 hover:underline font-medium">#{id}</button>
    ) : null);

    return (
        <div className="space-y-6">
            {/* Explanation + view switch */}
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-sm text-sky-800 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    
                    Montants (ou dettes) gelés avec « Geler » dans un lot confirmé. Ils ne reviennent plus dans les lots, sauf si la personne retravaille (dégel automatique) ou s'ils sont dégelés ici.
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => setView('current')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${view === 'current' ? 'bg-sky-600 text-white' : 'bg-white border border-sky-200 text-sky-700'}`}>
                        <FontAwesomeIcon icon={faSnowflake} className="mr-1" />Gelés
                    </button>
                    <button onClick={() => setView('history')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${view === 'history' ? 'bg-sky-600 text-white' : 'bg-white border border-sky-200 text-sky-700'}`}>
                        <FontAwesomeIcon icon={faHistory} className="mr-1" />Historique des gels
                    </button>
                </div>
            </div>

            {notice && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex justify-between">
                    <span>{notice}</span>
                    <button onClick={() => setNotice('')}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
            )}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError('')}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
            )}

            {view === 'current' && stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        ['Personnes', stats.persons, 'text-gray-900', false],
                        ['Montants gelés', stats.frozen_money, 'text-sky-700', true],
                        ['Dettes gelées', stats.frozen_debts, 'text-red-600', true],
                        ['Net gelé', stats.net, 'text-gray-900', true],
                    ].map(([label, value, cls, money]) => (
                        <div key={label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <p className="text-sm text-gray-600">{label}</p>
                            <p className={`text-xl font-bold ${cls}`}>{money ? `${fmt(value)} MAD` : value}</p>
                        </div>
                    ))}
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher une personne (nom, référence)..."
                        className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                    {loading && loadedView === view && (
                        <FontAwesomeIcon icon={faSpinner} spin className="absolute right-3 top-1/2 -translate-y-1/2 text-sky-500" />
                    )}
                </div>
                {view === 'history' && (
                    <select value={historyStatus} onChange={(e) => setHistoryStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg">
                        <option value="">Tous les statuts</option>
                        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                )}
            </div>

            {loading && loadedView !== view ? (
                <div className="p-8 text-center text-gray-500"><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />Chargement...</div>
            ) : view === 'current' ? (
                persons.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">Aucun montant gelé.</div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Personne</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant gelé</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Dettes</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gel</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {persons.map((p) => (
                                    <tr key={`${p.person_type}-${p.person_id}`} className="align-top hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">{p.name}</div>
                                            <div className="text-xs text-gray-500">{p.ref} · {PERSON_TYPE_LABELS[p.person_type] || p.person_type}</div>
                                            {p.archived && <div className="text-xs text-gray-400">Archivé</div>}
                                        </td>
                                        <td className="px-4 py-3 text-right text-sm font-semibold text-sky-700 whitespace-nowrap">{fmt(p.frozen_money)} MAD</td>
                                        <td className="px-4 py-3 text-right text-sm text-red-600 whitespace-nowrap">{p.frozen_debts ? `${fmt(p.frozen_debts)} MAD` : '-'}</td>
                                        <td className="px-4 py-3 text-right text-sm font-bold text-gray-900 whitespace-nowrap">{fmt(p.net)} MAD</td>
                                        <td className="px-4 py-3 text-sm">
                                            <div className="space-y-2">
                                                {p.cases.map((c) => (
                                                    <div key={c.id} className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 border border-sky-100 rounded-lg p-2">
                                                        <div className="text-xs text-gray-700">
                                                            <div><span className="font-semibold">{fmt(c.net)} MAD</span> · lot <BatchLink id={c.frozen_in_batch_id} /> · {fmtDate(c.frozen_at)}{c.frozen_by ? ` · par ${c.frozen_by}` : ''}</div>
                                                            {c.freeze_reason && <div className="text-gray-500 italic">« {c.freeze_reason} »</div>}
                                                        </div>
                                                        {p.can_unfreeze && (
                                                            <button
                                                                onClick={() => { setUnfreezeTarget({ person: p, caseItem: c }); setUnfreezeReason(''); }}
                                                                className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 whitespace-nowrap"
                                                            >
                                                                <FontAwesomeIcon icon={faFire} className="mr-1" />Dégeler
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : (
                <>
                    {history.length === 0 ? (
                        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">Aucun gel.</div>
                    ) : (
                        <div className="space-y-3">
                            {history.map((c) => {
                                const st = STATUS[c.status] || { label: c.status, cls: 'bg-gray-100 text-gray-600' };
                                const open = !!expanded[c.id];
                                return (
                                    <div key={c.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                                        <button onClick={() => setExpanded({ ...expanded, [c.id]: !open })} className="w-full p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-left">
                                            <div>
                                                <div className="font-medium text-gray-900">{c.name} <span className="text-xs text-gray-500">{c.ref} · {PERSON_TYPE_LABELS[c.person_type] || c.person_type}</span></div>
                                                <div className="text-xs text-gray-600">
                                                    Gel #{c.id} · {fmt(c.amount)} MAD · gelé dans le lot #{c.frozen_in_batch_id} le {fmtDate(c.frozen_at)}{c.frozen_by ? ` par ${c.frozen_by}` : ''}
                                                    {c.paid_in_batch_id ? ` · payé dans le lot #${c.paid_in_batch_id}` : ''}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                                                <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="text-gray-400" />
                                            </div>
                                        </button>
                                        {open && (
                                            <div className="border-t border-gray-100 px-4 py-3">
                                                <ol className="relative border-l border-sky-200 ml-2 space-y-3">
                                                    {c.events.map((e) => (
                                                        <li key={e.id} className="ml-4">
                                                            <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-sky-400 border border-white" />
                                                            <div className="text-sm text-gray-900">
                                                                {EVENT_LABELS[e.event] || e.event}
                                                                {e.amount !== null && <span className="ml-2 font-semibold">{fmt(e.amount)} MAD</span>}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {fmtDate(e.created_at)}{e.user_name ? ` · ${e.user_name}` : ''}
                                                                {e.batch_id ? <> · lot <BatchLink id={e.batch_id} /></> : null}
                                                                {e.payment_line_id ? ` · ligne #${e.payment_line_id}` : ''}
                                                            </div>
                                                            {e.reason && <div className="text-xs text-gray-600 italic">« {e.reason} »</div>}
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {historyLastPage > 1 && (
                        <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-3 text-sm text-gray-700">
                            <span>{historyTotal} gel(s) · page {historyPage} / {historyLastPage}</span>
                            <div className="flex gap-2">
                                <button disabled={historyPage === 1} onClick={() => setHistoryPage(historyPage - 1)} className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50"><FontAwesomeIcon icon={faChevronLeft} /></button>
                                <button disabled={historyPage === historyLastPage} onClick={() => setHistoryPage(historyPage + 1)} className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50"><FontAwesomeIcon icon={faChevronRight} /></button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Unfreeze modal */}
            {unfreezeTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...unfreezeDismiss}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                        <div className="flex items-center justify-between p-6 border-b border-amber-200 bg-amber-50 rounded-t-xl">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Dégeler</h3>
                                <p className="text-sm text-gray-600">{unfreezeTarget.person.name} · {fmt(unfreezeTarget.caseItem.net)} MAD</p>
                            </div>
                            <button onClick={() => setUnfreezeTarget(null)} className="text-gray-400 hover:text-gray-600" disabled={unfreezing}>
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                                Le montant redevient payable : il sera repris dans le prochain lot généré (un lot déjà en brouillon doit être recalculé pour l'inclure).
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Motif du dégel <span className="text-red-500">*</span></label>
                                <textarea
                                    value={unfreezeReason}
                                    onChange={(e) => setUnfreezeReason(e.target.value)}
                                    rows="3"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    placeholder="Ex. : la personne a demandé son paiement..."
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                            <button onClick={() => setUnfreezeTarget(null)} disabled={unfreezing} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Annuler</button>
                            <button onClick={submitUnfreeze} disabled={unfreezing || !unfreezeReason.trim()} className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg disabled:opacity-50">
                                {unfreezing ? <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" />Dégel...</> : 'Dégeler'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FrozenPayments;
